import { normalizeReflectionEmotion, DEFAULT_EMOTION_WHEN_MISSING, getEmotionMeta } from "../constants/emotions.js";

function addField(fields, key, value) {
  if (value == null || value === "") return;
  if (Array.isArray(value) && value.length === 0) return;
  fields[key] = value;
}

/**
 * Builds a flat object of translatable strings/arrays for translateHistoryContent.
 * Works with history card items and live Quick Clarity insight objects.
 */
export function buildInsightTranslationFields(source) {
  if (!source) return {};

  const fields = {};

  addField(fields, "reflection", source.reflection);
  addField(fields, "eventReflection", source.eventReflection);
  addField(fields, "interpretationReflection", source.interpretationReflection);
  addField(fields, "evidenceReflection", source.evidenceReflection);
  addField(fields, "responseStatement", source.responseStatement);
  addField(fields, "behaviorAction", source.behaviorAction);
  addField(fields, "understanding", source.understanding);
  addField(fields, "primaryInsight", source.primaryInsight);
  addField(fields, "userInterpretation", source.userInterpretation);
  addField(fields, "clarifyingQuestion", source.clarifyingQuestion);
  addField(fields, "patternLabel", source.patternLabel);
  addField(fields, "patternWhy", source.patternWhy);
  addField(fields, "supportMessage", source.supportMessage);

  if (Array.isArray(source.observedFacts) && source.observedFacts.length > 0) {
    fields.observedFacts = source.observedFacts;
  }
  if (Array.isArray(source.unknowns) && source.unknowns.length > 0) {
    fields.unknowns = source.unknowns;
  }
  if (Array.isArray(source.emotionBeneath) && source.emotionBeneath.length > 0) {
    fields.emotionBeneath = source.emotionBeneath;
  }

  if (Array.isArray(source.suggestedActions) && source.suggestedActions.length > 0) {
    fields.suggestedActions = source.suggestedActions;
  }

  const emotionRaw = (source.sentiment || "").trim();
  if (emotionRaw) {
    const normalized = normalizeReflectionEmotion(emotionRaw || DEFAULT_EMOTION_WHEN_MISSING);
    fields.emotionLabel = normalized;
    const meta = getEmotionMeta(normalized);
    if (meta?.meaning) fields.emotionMeaning = meta.meaning;
    const category = source.emotionCategory || meta?.category;
    if (category) fields.emotionCategory = category;
  } else if (source.emotionCategory) {
    fields.emotionCategory = source.emotionCategory;
  }

  return fields;
}

/** Maps a stored insight row into translatable fields. */
export function buildTranslationFieldsFromHistoryItem(item) {
  if (!item) return {};
  return buildInsightTranslationFields(item);
}

/** Maps a live Quick Clarity insight payload into translatable fields. */
export function buildTranslationFieldsFromQuickClarityInsight(insight) {
  if (!insight) return {};
  const beneath = Array.isArray(insight.emotions?.beneath)
    ? insight.emotions.beneath.map((b) => (typeof b === "string" ? b : b?.label)).filter(Boolean)
    : [];

  const sentiment = insight.emotions?.primary?.label || insight.sentiment;

  return buildInsightTranslationFields({
    reflection: insight.reflection || insight.entry_text,
    understanding: insight.understanding,
    primaryInsight: insight.primary_insight,
    userInterpretation: insight.user_interpretation,
    clarifyingQuestion: insight.clarifying_question,
    observedFacts: insight.observed_or_reported_facts,
    unknowns: insight.unknowns,
    patternLabel: insight.possible_cbt_pattern?.label,
    patternWhy: insight.possible_cbt_pattern?.why_it_may_apply,
    emotionBeneath: beneath,
    sentiment,
    emotionCategory: insight.emotion_category,
    supportMessage: insight.support_message,
    suggestedActions: Array.isArray(insight.suggested_actions) ? insight.suggested_actions : undefined
  });
}
